import { useContext } from "react";
import { ChooseTreatmentContext } from "../FreeDosageLayoutV2";
import { IoMdClose } from "react-icons/io";
import { RouterCartContext } from "../../ChooseTreatmentRouter";
import { SortBy } from "../../filters/SortBy";
import { FilterOptionsSubMenu } from "../../filters/FilterOptionsSubMenu";
import { ORDER_FLOWS } from "../../../../utils/consts";
import { translate } from "../../../../utils/translator";
import { useDragModal } from "../../../../customHooks/useDragModal";

const scrollToTop = () => {
    window.scrollTo({
        top: 0,
        behavior: 'smooth'  // Optional, smooth scroll behavior
    });
}

type FilterOption = {
    value: string;
    isSelected: boolean;
    name: string;
    keyWordTitle: string;
}

type FilterData = {
    [key: string]: FilterOption[];
}

type FilterMenuProps = {
    filterData: FilterData;
    hidePremiumOrRegularFilter: boolean;
}

export const FilterMenu: React.FC<FilterMenuProps> = ({ filterData, hidePremiumOrRegularFilter }) => {
    const chooseTreatmentContext = useContext(ChooseTreatmentContext);

    const language = chooseTreatmentContext?.language || 'de';
    const filtersOrder = ['favourite', 'genetic', 'type', 'price', 'effects', 'medical_effects', 'Delivery Type', 'thc', 'cbd'];
    const activeFiltersKeys = Object.keys(filterData);
    const orderedFiltersKeys = filtersOrder.filter((f) => activeFiltersKeys.includes(f));
    const isOnlyRegulars = chooseTreatmentContext?.PurchaseTierFilter === 'regular'
    const routerCartContext: any = useContext(RouterCartContext)
    const cart = routerCartContext.cartData;
    const isMobile = window.innerWidth < 768;
    const fromExternalPharmacy = (cart.flow === ORDER_FLOWS.CANNALEO_PRE_SELECTED || cart.flow === ORDER_FLOWS.FLOWZZ_PRE_SELECTED)

    // Use drag-to-close functionality
    const {
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd,
        handleMouseDown,
        currentTranslateY,
        modalDragTransition,
        shouldPreventClick,
        closeModal,
    } = useDragModal({
        openingAnimationDuration: 500,
        onClose: () => chooseTreatmentContext?.setIsFilterMenuOpen(false),
        shouldShow: chooseTreatmentContext?.isFilterMenuOpen || false,
        closeModalDelay: 0,
    });

    if (!chooseTreatmentContext) {
        return null;
    }

    return (
        <div
            id="filters-modal"
            className={`
                h-screen 
                w-screen 
                fixed 
                top-0 
                right-0 
                grid 
                grid-rows-[15%_1fr] md:grid-rows-none
                grid-cols-1 md:grid-cols-[1fr_minmax(500px,auto)]
                transition-all 
                ease-in-out 
                duration-500
                z-50
                ${chooseTreatmentContext.isFilterMenuOpen ? 'bg-black/50' : 'bg-transparent pointer-events-none'}
            `}
        >
            {/* background left of the modal */}
            <div onClick={() => closeModal()} className="w-full h-full touch-none" onTouchMove={(e) => e.preventDefault()} />

            {/* Modal body */}
            <div
                id="choose-treatment-filters-modal-body"
                className={`
                    flex 
                    flex-col 
                    justify-between
                    bg-white
                    md:pl-[35px] 
                    pl-[15px] 
                    md:pr-[15px] 
                    pr-[5px] 
                    pb-4
                    [&>:not(.custom-padding-right)]:mr-[10px] 
                    md:[&>:not(.custom-padding-right)]:mr-[20px] 
                    overflow-hidden
                    transform 
                    transition-transform 
                    duration-500 
                    ease-in-out                    
                    `}
                style={{
                    borderTopLeftRadius: '30px',
                    borderTopRightRadius: isMobile ? '30px' : undefined,
                    transform: isMobile 
                        ? `translateY(${currentTranslateY}px)`
                        : chooseTreatmentContext.isFilterMenuOpen
                            ? 'translate(0)'
                            : 'translateX(100%)',
                    transition: isMobile ? modalDragTransition : 'transform 500ms ease-in-out',
                }}
            >

                {/* grabber for mnobile layout to open/close the modal */}
                {isMobile &&
                    <div
                        id="grabber-wrapper"
                        className="flex items-center justify-center pt-3 pb-4 touch-none select-none"
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        onMouseDown={handleMouseDown}
                        onClick={(e) => {
                            // Prevent click from firing if we've been dragging, closing, or opening
                            if (shouldPreventClick()) {
                                e.preventDefault();
                                e.stopPropagation();
                                return;
                            }
                        }}
                    >
                        <div
                            id="select-treatment--mobile-modal-gaber"
                            className="
                                cursor-pointer
                                bg-[#EDEDED]
                                rounded-[10px]
                                w-1/2
                                h-[5px]
                            "
                        >
                        </div>
                    </div>
                }

                {/* top title + right close button */}
                <div className="flex justify-center md:justify-between items-center mt-0 mb-2 md:mt-4">
                    <p className="font-normal text-[21px] mb-2">{translate(language, 'choose_treatment', 'filters/title')}</p>

                    {/* Close button visible only on desktop */}
                    <p
                        id="select-treatment-desktop-modal-close-button"
                        className="cursor-pointer font-medium text-lg"
                        style={{ display: isMobile ? 'none' : 'block' }}
                        onClick={(e) => closeModal()}
                    >
                        <IoMdClose />
                    </p>
                </div>


                <div
                    id="choose-treatment-filters-scrollable-area"
                    className="h-full pr-[5px] md:pr-[15px] mt-5 pb-1 custom-padding-right"
                    style={{ overflowY: "scroll", overscrollBehavior: "contain" }}
                >

                    <div
                        id="filters-modal-menu"
                        style={{ border: "1px solid #DBE1EB", borderRadius: "15px" }}
                    >

                        {/* Sorting */}
                        <SortBy
                            sortBy={chooseTreatmentContext.sortBy}
                            setSortBy={chooseTreatmentContext.setSortBy}
                            isOnlyRegulars={isOnlyRegulars}
                            language={language}
                        />

                        {/* Filters menu*/}
                        {orderedFiltersKeys.map((filterKey: string, idx: number) => {
                            return (
                                // price filters should be hidden for regulars + preselected flows
                                filterData[filterKey] && (filterKey !== "price" || (!isOnlyRegulars && !fromExternalPharmacy)) &&
                                // hide the product-type filter when it has only one option (nothing to filter)
                                (filterKey !== "type" || filterData[filterKey].length > 1) &&
                                <div
                                    className='flex flex-col gap-5'
                                    key={filterKey}
                                >
                                    <FilterOptionsSubMenu
                                        key={filterKey}
                                        filterOptions={filterData[filterKey]}
                                        keyWord={filterKey}
                                        toggleFilter={chooseTreatmentContext.toggleFilter}
                                        isLastChild={idx === orderedFiltersKeys.length - 1}
                                    />
                                </div>
                            )
                        })}
                    </div>
                </div>

                {/* general filter buttons: apply + clear */}
                <div className="mt-5 sticky">
                    <div 
                        id="filters-submit" 
                        className="w-full font-semibold text-center cursor-pointer text-sm bg-primary-button-gradient text-white p-3 rounded-md" 
                        onClick={(e) => {
                            closeModal();
                            scrollToTop();
                        }}
                    >
                        {translate(language, 'choose_treatment', 'filters/apply')} ({chooseTreatmentContext?.appliedFiltersCount})
                    </div>
                    <div 
                        className="w-full font-semibold text-center cursor-pointer text-sm underline text-[#616161] p-3 rounded-md" 
                        onClick={(e) => {
                            chooseTreatmentContext.resetAllFilters();
                        }}
                    >
                        {translate(language, 'choose_treatment', 'filters/reset')}
                    </div>
                </div>

                {/* transparent scrollbar-gutter */}
                <style>{`.custom-padding-right::-webkit-scrollbar { background-color: transparent; }`}</style>
            </div>
        </div>
    )
}